import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import CommonWrapper from '../../../../components/ui_components/FounderWrapper';
import colors from '../../../../styles/colors';
import { apiGetExpansions, apiGetExpansion, apiDeleteExpansion } from '../../../../api/expansionApi';
import type { Expansion } from '../../../../types/models/expansion';
import ExpansionCreateModal from './ExpansionCreateModal';
import ExpansionAccordion from './ExpansionAccordion';
import { apiSetAuthorByExpansion } from '../../../../api/cardApi';
import { apiListAllUsers } from '../../../../api/userApi';
import type { UserListItem } from '../../../../types/models/user';

const PageInner = styled.div`
  padding: 32px 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 28px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: gold;
  margin: 0;
  flex: 1;
`;

const NewBtn = styled.button`
  padding: 8px 16px;
  background-color: ${colors.bgColorBordeaux};
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background-color: ${colors.bgColorRed}; }
`;

const StatusText = styled.p`
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  margin-top: 40px;
`;

const ErrorText = styled.p`
  color: #dc3545;
  text-align: center;
  margin-top: 40px;
`;

const ExpRow = styled.div<{ expanded: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 18px;
  background-color: ${colors.bgColorBlack2};
  border: 1px solid ${({ expanded }) => (expanded ? 'rgba(255,215,0,0.35)' : 'rgba(255,255,255,0.08)')};
  border-radius: ${({ expanded }) => (expanded ? '6px 6px 0 0' : '6px')};
  margin-bottom: ${({ expanded }) => (expanded ? '0' : '8px')};
  transition: border-color 0.15s;
`;

const ExpName = styled.span`
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  flex: 1;
`;

const ExpDate = styled.span`
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
`;

const ActiveBadge = styled.span<{ active: boolean }>`
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${({ active }) => (active ? '#28a745' : '#555')};
  color: #fff;
`;

const EditBtn = styled.button<{ open?: boolean }>`
  padding: 6px 14px;
  background-color: ${({ open }) => (open ? 'rgba(255,215,0,0.15)' : 'transparent')};
  color: gold;
  border: 1px solid gold;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background-color: rgba(255, 215, 0, 0.15); }
`;

const DeleteExpBtn = styled.button`
  padding: 6px 12px;
  background-color: transparent;
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.5);
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background-color: rgba(220, 53, 69, 0.1); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const LoadingText = styled.span`
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
  flex-shrink: 0;
`;

const AssignAuthorBtn = styled.button`
  padding: 6px 12px;
  background-color: transparent;
  color: rgba(255, 165, 0, 0.85);
  border: 1px solid rgba(255, 165, 0, 0.4);
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background-color: rgba(255, 165, 0, 0.1); }
`;

// ─── Assign Author Modal ──────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const ModalBox = styled.div`
  width: 100%;
  max-width: 500px;
  background-color: ${colors.bgColorBlack2};
  border: 1px solid rgba(255, 215, 0, 0.25);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  gap: 10px;
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  margin: 0;
  flex: 1;
`;

const ModalCloseBtn = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.45);
  font-size: 20px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  &:hover { color: #fff; }
`;

const ModalBody = styled.div`
  padding: 18px 18px 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const WarningBox = styled.div`
  background-color: rgba(220, 53, 69, 0.08);
  border: 1px solid rgba(220, 53, 69, 0.35);
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 13px;
  color: #e07070;
  line-height: 1.5;
`;

const ModeToggleRow = styled.div`
  display: flex;
  gap: 8px;
`;

const ModeBtn = styled.button<{ $active: boolean }>`
  padding: 5px 16px;
  border-radius: 4px;
  border: 1px solid ${({ $active }) => ($active ? 'gold' : 'rgba(255,255,255,0.2)')};
  background-color: ${({ $active }) => ($active ? 'rgba(255,215,0,0.12)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'gold' : 'rgba(255,255,255,0.45)')};
  font-size: 13px;
  cursor: pointer;
`;

const FieldLabel = styled.label`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: block;
  margin-bottom: 6px;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background-color: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 14px;
  box-sizing: border-box;
  &:focus { outline: none; border-color: gold; }
  &::placeholder { color: rgba(255,255,255,0.3); }
`;

const SearchWrapper = styled.div`
  position: relative;
`;

const DropdownList = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background-color: #1e1e1e;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  list-style: none;
  margin: 0;
  padding: 4px 0;
  max-height: 180px;
  overflow-y: auto;
  z-index: 10;
`;

const DropdownItem = styled.li`
  padding: 7px 12px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  &:hover { background-color: rgba(255, 255, 255, 0.07); }
`;

const SelectedChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background-color: rgba(255, 215, 0, 0.12);
  border: 1px solid rgba(255, 215, 0, 0.35);
  border-radius: 12px;
  font-size: 13px;
  color: gold;
`;

const ChipRemove = styled.button`
  background: none;
  border: none;
  color: rgba(255, 215, 0, 0.6);
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  line-height: 1;
  &:hover { color: gold; }
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const ConfirmBtn = styled.button<{ $disabled?: boolean }>`
  padding: 8px 22px;
  background-color: ${({ $disabled }) => ($disabled ? 'rgba(255,215,0,0.05)' : 'rgba(255,215,0,0.15)')};
  border: 1px solid ${({ $disabled }) => ($disabled ? 'rgba(255,215,0,0.2)' : 'gold')};
  border-radius: 4px;
  color: ${({ $disabled }) => ($disabled ? 'rgba(255,215,0,0.35)' : 'gold')};
  font-size: 14px;
  font-weight: 600;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
`;

const CancelBtn = styled.button`
  padding: 8px 18px;
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 14px;
  cursor: pointer;
  &:hover { border-color: rgba(255, 255, 255, 0.3); color: #fff; }
`;

const SuccessMsg = styled.p`
  font-size: 13px;
  color: #5adf7a;
  margin: 0;
`;

const ErrorMsg = styled.p`
  font-size: 13px;
  color: #e05555;
  margin: 0;
`;

const ExpThumb = styled.img`
  width: 72px;
  height: 100px;
  object-fit: cover;
  border-radius: 3px;
  flex-shrink: 0;
  background-color: #2a2a2a;
`;

const ExpThumbPlaceholder = styled.div`
  width: 72px;
  height: 100px;
  border-radius: 3px;
  background-color: #2a2a2a;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  color: rgba(255,255,255,0.2);
  text-align: center;
  line-height: 1.2;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const ExpansionListPage: React.FC = () => {
  const [expansions, setExpansions] = useState<Expansion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Accordion state
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [detailCache, setDetailCache] = useState<Record<number, Expansion>>({});
  const [loadingDetailId, setLoadingDetailId] = useState<number | null>(null);
  const [deletingExpId, setDeletingExpId] = useState<number | null>(null);

  // Assign author state
  const [assignExp, setAssignExp] = useState<Expansion | null>(null);
  const [assignMode, setAssignMode] = useState<'user' | 'text'>('user');
  const [assignUsers, setAssignUsers] = useState<UserListItem[] | null>(null);
  const [assignUsersLoading, setAssignUsersLoading] = useState(false);
  const [assignUserQuery, setAssignUserQuery] = useState('');
  const [assignUserOpen, setAssignUserOpen] = useState(false);
  const [assignUserId, setAssignUserId] = useState<number | null>(null);
  const [assignUserLabel, setAssignUserLabel] = useState('');
  const [assignText, setAssignText] = useState('');
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignSuccess, setAssignSuccess] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);
  const assignSearchRef = useRef<HTMLDivElement>(null);

  const handleOpenAssign = async (exp: Expansion) => {
    setAssignExp(exp);
    setAssignMode('user');
    setAssignUserId(null);
    setAssignUserLabel('');
    setAssignUserQuery('');
    setAssignUserOpen(false);
    setAssignText('');
    setAssignSuccess(null);
    setAssignError(null);
    if (!assignUsers) {
      setAssignUsersLoading(true);
      try {
        const list = await apiListAllUsers();
        setAssignUsers(list);
      } catch {
        setAssignUsers([]);
      } finally {
        setAssignUsersLoading(false);
      }
    }
  };

  const handleAssignConfirm = async () => {
    if (!assignExp) return;
    if (assignMode === 'user' && !assignUserId) { setAssignError('Seleziona un utente.'); return; }
    if (assignMode === 'text' && !assignText.trim()) { setAssignError('Inserisci il nome autore.'); return; }
    setAssignSubmitting(true);
    setAssignError(null);
    setAssignSuccess(null);
    try {
      const res = await apiSetAuthorByExpansion(
        assignExp.id,
        assignMode === 'user' ? assignUserId : null,
        assignMode === 'text' ? assignText.trim() : null,
      );
      setAssignSuccess(`${res.message} (${res.cards_updated} carte aggiornate)`);
    } catch (err: any) {
      setAssignError(err?.message ?? 'Errore durante l\'operazione.');
    } finally {
      setAssignSubmitting(false);
    }
  };

  const filteredAssignUsers = (assignUsers ?? []).filter((u) => {
    if (!assignUserQuery.trim()) return true;
    const q = assignUserQuery.toLowerCase();
    return u.username.toLowerCase().includes(q) || (u.nickname_forum?.toLowerCase().includes(q) ?? false);
  });

  useEffect(() => {
    let isMounted = true;
    apiGetExpansions()
      .then((data) => {
        if (isMounted) setExpansions(Array.isArray(data) ? data : ((data as any).data ?? []));
      })
      .catch(() => {
        if (isMounted) setError('Errore durante il caricamento delle espansioni.');
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });
    return () => { isMounted = false; };
  }, []);

  const handleToggleExpand = async (id: number) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (!detailCache[id]) {
      setLoadingDetailId(id);
      try {
        const detail = await apiGetExpansion(id);
        setDetailCache((prev) => ({ ...prev, [id]: detail }));
      } finally {
        setLoadingDetailId(null);
      }
    }
  };

  const handleDeleteExpansion = async (exp: Expansion) => {
    if (!window.confirm(`Eliminare l'espansione "${exp.name}"? L'operazione è irreversibile.`)) return;
    setDeletingExpId(exp.id);
    try {
      await apiDeleteExpansion(exp.id);
      setExpansions((prev) => prev.filter((e) => e.id !== exp.id));
      if (expandedId === exp.id) setExpandedId(null);
    } catch (err: any) {
      alert(err?.message ?? 'Eliminazione fallita.');
    } finally {
      setDeletingExpId(null);
    }
  };

  const handleDetailUpdated = (updated: Expansion) => {
    setDetailCache((prev) => ({ ...prev, [updated.id]: updated }));
    setExpansions((prev) =>
      prev.map((e) =>
        e.id === updated.id
          ? { ...e, name: updated.name, release_date: updated.release_date, is_active: updated.is_active, expansion_path: updated.expansion_path }
          : e
      )
    );
  };

  const storageUrl = process.env.REACT_APP_STORAGE_URL ?? '';

  return (
    <>
      <CommonWrapper>
        <PageInner>
          <PageHeader>
            <PageTitle>Gestisci Espansioni</PageTitle>
            <NewBtn onClick={() => setIsModalOpen(true)}>
              + Nuova Espansione
            </NewBtn>
          </PageHeader>

          {isLoading && <StatusText>Caricamento...</StatusText>}
          {error && <ErrorText>{error}</ErrorText>}
          {!isLoading && !error && expansions.length === 0 && (
            <StatusText>Nessuna espansione trovata.</StatusText>
          )}

          {expansions.map((exp) => {
            const isExpanded = expandedId === exp.id;
            const isLoadingThis = loadingDetailId === exp.id;
            const detail = detailCache[exp.id];

            return (
              <div key={exp.id}>
                <ExpRow expanded={isExpanded}>
                  {exp.expansion_path ? (
                    <ExpThumb
                      src={exp.expansion_path.startsWith('http') ? exp.expansion_path : storageUrl + exp.expansion_path}
                      alt={exp.name}
                    />
                  ) : (
                    <ExpThumbPlaceholder>No img</ExpThumbPlaceholder>
                  )}
                  <ExpName>{exp.name}</ExpName>
                  <ExpDate>{exp.release_date}</ExpDate>
                  <ActiveBadge active={exp.is_active}>
                    {exp.is_active ? 'Attiva' : 'Non attiva'}
                  </ActiveBadge>
                  {isLoadingThis && <LoadingText>Caricamento...</LoadingText>}
                  <AssignAuthorBtn onClick={() => handleOpenAssign(exp)}>
                    Assegna Autore
                  </AssignAuthorBtn>
                  <DeleteExpBtn
                    onClick={() => handleDeleteExpansion(exp)}
                    disabled={deletingExpId === exp.id || isLoadingThis}
                  >
                    {deletingExpId === exp.id ? '...' : 'Elimina'}
                  </DeleteExpBtn>
                  <EditBtn
                    open={isExpanded}
                    onClick={() => handleToggleExpand(exp.id)}
                    disabled={isLoadingThis}
                  >
                    {isExpanded ? '▲ Chiudi' : '▼ Modifica'}
                  </EditBtn>
                </ExpRow>

                {isExpanded && detail && (
                  <ExpansionAccordion
                    key={exp.id}
                    expansion={detail}
                    onDetailUpdated={handleDetailUpdated}
                  />
                )}
              </div>
            );
          })}
        </PageInner>
      </CommonWrapper>

      {isModalOpen && (
        <ExpansionCreateModal
          onClose={() => setIsModalOpen(false)}
          onCreated={(exp) => setExpansions((prev) => [exp, ...prev])}
        />
      )}

      {assignExp && (
        <ModalOverlay onClick={(e) => { if (e.target === e.currentTarget) setAssignExp(null); }}>
          <ModalBox>
            <ModalHeader>
              <ModalTitle>Assegna Autore — {assignExp.name}</ModalTitle>
              <ModalCloseBtn type="button" onClick={() => setAssignExp(null)}>✕</ModalCloseBtn>
            </ModalHeader>
            <ModalBody>
              <WarningBox>
                Attenzione: questa operazione sovrascriverà l'autore di <strong>tutte le carte</strong> dell'espansione "{assignExp.name}". L'operazione è irreversibile.
              </WarningBox>

              <div>
                <FieldLabel>Tipo autore</FieldLabel>
                <ModeToggleRow>
                  <ModeBtn $active={assignMode === 'user'} onClick={() => { setAssignMode('user'); setAssignText(''); }}>
                    Utente registrato
                  </ModeBtn>
                  <ModeBtn $active={assignMode === 'text'} onClick={() => { setAssignMode('text'); setAssignUserId(null); setAssignUserLabel(''); setAssignUserQuery(''); }}>
                    Testo libero
                  </ModeBtn>
                </ModeToggleRow>
              </div>

              {assignMode === 'user' && (
                <div>
                  <FieldLabel>Utente</FieldLabel>
                  {assignUserId ? (
                    <SelectedChip>
                      {assignUserLabel}
                      <ChipRemove type="button" onClick={() => { setAssignUserId(null); setAssignUserLabel(''); }}>×</ChipRemove>
                    </SelectedChip>
                  ) : (
                    <SearchWrapper ref={assignSearchRef}>
                      <TextInput
                        placeholder={assignUsersLoading ? 'Caricamento utenti...' : 'Cerca utente...'}
                        value={assignUserQuery}
                        disabled={assignUsersLoading}
                        onChange={(e) => { setAssignUserQuery(e.target.value); setAssignUserOpen(true); }}
                        onFocus={() => setAssignUserOpen(true)}
                      />
                      {assignUserOpen && filteredAssignUsers.length > 0 && (
                        <DropdownList>
                          {filteredAssignUsers.slice(0, 30).map((u) => (
                            <DropdownItem
                              key={u.id}
                              onMouseDown={() => {
                                setAssignUserId(u.id);
                                setAssignUserLabel(u.username);
                                setAssignUserQuery('');
                                setAssignUserOpen(false);
                              }}
                            >
                              {u.username}
                              {u.nickname_forum && (
                                <span style={{ color: 'rgba(255,255,255,0.4)', marginLeft: 6, fontSize: 11 }}>
                                  "{u.nickname_forum}"
                                </span>
                              )}
                            </DropdownItem>
                          ))}
                        </DropdownList>
                      )}
                    </SearchWrapper>
                  )}
                </div>
              )}

              {assignMode === 'text' && (
                <div>
                  <FieldLabel>Nome autore</FieldLabel>
                  <TextInput
                    placeholder="Es. Nome Cognome"
                    value={assignText}
                    onChange={(e) => setAssignText(e.target.value)}
                  />
                </div>
              )}

              {assignSuccess && <SuccessMsg>{assignSuccess}</SuccessMsg>}
              {assignError && <ErrorMsg>{assignError}</ErrorMsg>}

              <ModalFooter>
                <CancelBtn type="button" onClick={() => setAssignExp(null)}>Annulla</CancelBtn>
                <ConfirmBtn
                  type="button"
                  $disabled={assignSubmitting}
                  disabled={assignSubmitting}
                  onClick={handleAssignConfirm}
                >
                  {assignSubmitting ? 'Salvataggio...' : 'Conferma'}
                </ConfirmBtn>
              </ModalFooter>
            </ModalBody>
          </ModalBox>
        </ModalOverlay>
      )}
    </>
  );
};

export default ExpansionListPage;
